import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import AdBanner from '../AdPage/AdBanner';

// Register necessary components for Chart.js
ChartJS.register(ArcElement, ChartTooltip, Legend);

const IncomeTaxCalculator = () => {
  const [assessmentYear, setAssessmentYear] = useState('2021-2022');
  const [ageCategory, setAgeCategory] = useState('below 60');
  const [activeSection, setActiveSection] = useState(null);

  const [income, setIncome] = useState({
    salary: 0,
    otherSources: 0,
    interest: 0,
    rentalIncome: 0,
    homeLoanInterestSelf: 0,
    homeLoanInterestLetOut: 0,
  });

  const [deductions, setDeductions] = useState({
    basic80C: 0,
    nps80CCD: 0,
    medical80D: 0,
    charity80G: 0,
    eduLoan80E: 0,
    savingsInterest80TTA: 0,
  });

  const [hraExemption, setHraExemption] = useState({
    basicSalary: 0,
    daReceived: 0,
    hraReceived: 0,
    rentPaid: 0,
  });

  const [results, setResults] = useState({
    oldRegimeTax: 0,
    newRegimeTax: 0,
  });

  const handleIncomeChange = (e) => {
    const { name, value } = e.target;
    setIncome({
      ...income,
      [name]: parseFloat(value) || 0,
    });
  };

  const handleDeductionsChange = (e) => {
    const { name, value } = e.target;
    setDeductions({
      ...deductions,
      [name]: parseFloat(value) || 0,
    });
  };

  const handleHRAExemptionChange = (e) => {
    const { name, value } = e.target;
    setHraExemption({
      ...hraExemption,
      [name]: parseFloat(value) || 0,
    });
  };

  const calculateTax = () => {
    // Placeholder logic for calculating tax
    const totalIncome = Object.values(income).reduce((a, b) => a + b, 0);
    const totalDeductions = Object.values(deductions).reduce((a, b) => a + b, 0);
    const hraExemptionTotal = Object.values(hraExemption).reduce((a, b) => a + b, 0);

    const taxableIncomeOld = totalIncome - totalDeductions - hraExemptionTotal;
    const taxableIncomeNew = totalIncome; // Assuming no deductions in the new regime

    const oldRegimeTax = taxableIncomeOld * 0.1; // Placeholder calculation
    const newRegimeTax = taxableIncomeNew * 0.15; // Placeholder calculation

    setResults({
      oldRegimeTax: oldRegimeTax.toFixed(2),
      newRegimeTax: newRegimeTax.toFixed(2),
    });
  };

  const toggleSection = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  const data = {
    labels: ['Old Regime Tax', 'New Regime Tax'],
    datasets: [
      {
        label: 'Tax Amount',
        data: [results.oldRegimeTax, results.newRegimeTax],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is the difference between the old and new tax regimes?',
        answer: 'The old regime allows various deductions and exemptions, while the new regime offers lower tax rates but does not allow most exemptions or deductions.',
      },
      {
        question: 'Can I switch between tax regimes every year?',
        answer: 'Salaried individuals can switch every financial year. Individuals with business income can switch only once and then must continue with the chosen regime.',
      },
      {
        question: 'Is the standard deduction available in the new regime?',
        answer: 'Yes, a standard deduction of ₹75,000 is available for salaried and pensioned individuals under the new regime (as per Budget 2024 updates).',
      },
      {
        question: 'What is Section 87A rebate?',
        answer: 'It provides a rebate of up to ₹60,000 for individuals with taxable income up to ₹12 lakh under the new regime.',
      },
      {
        question: 'Are HRA and LTA exemptions available in the new regime?',
        answer: 'No, common exemptions like HRA and LTA are not available in the new tax regime.',
      },
      {
        question: 'How is income from house property taxed?',
        answer: 'For let-out property, income is taxable after deducting municipal taxes and a standard 30% deduction. Self-occupied property income is considered nil.',
      },
      {
        question: 'Can I claim deductions for home loan interest under the new regime?',
        answer: 'No, deductions like Section 24(b) for home loan interest are not allowed under the new regime.',
      },
      {
        question: 'Is income from fixed deposits taxable?',
        answer: 'Yes, interest earned from fixed deposits is fully taxable under both regimes as "Income from Other Sources".',
      },
      {
        question: 'What is the tax treatment for capital gains?',
        answer: 'Capital gains are taxed separately based on asset type and holding period, irrespective of the chosen tax regime.',
      },
      {
        question: 'Are senior citizens exempt from filing returns?',
        answer: 'Senior citizens with income below the basic exemption limit are not required to file returns. Super senior citizens (80+) may get additional relief.',
      },
      {
        question: 'How is agricultural income treated?',
        answer: 'Agricultural income is tax-exempt but may be used for calculating the applicable tax slab under certain conditions.',
      },
      {
        question: 'Can NRIs use the income tax calculator?',
        answer: 'Yes, NRIs can use the calculator by selecting their residential status correctly.',
      },
      {
        question: 'What documents are needed to file returns?',
        answer: 'Common documents include Form 16, interest certificates, investment proofs, capital gains statements, and bank account details.',
      },
      {
        question: 'Is advance tax applicable to salaried individuals?',
        answer: 'Yes, if the total tax liability exceeds ₹10,000 after TDS, salaried individuals must pay advance tax in quarterly installments.',
      },
      {
        question: 'How can I pay my income tax online?',
        answer: 'You can pay through the official Income Tax Department portal using net banking, UPI, debit card, or payment gateway options.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
    <Helmet>
         <title>Income Tax Calculator – Estimate Tax Liability Easily</title>
        <meta  name='description' content='Income Tax Calculator: Check how to calculate income tax online using Income Tax Calculator 2024. Calculate income tax on salary, for FY 2024-25; on TheFinmaster'/>
        <meta name='keywords' content='tax calculator cleartax, tax calculator 2024, salary tax calculator, cleartax tax calculator, online income tax calculator, long term capital gain tax rate, salary income tax calculator, calculate tds on salary' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/income-tax-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Income Tax Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
       <div className="w-full flex justify-center items-center px-4 mt-20">
      <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
      <AdBanner displayOrder="1" />
      </div>
    </div>
    <div className='py-4'>

    
    <div className="container mx-auto p-4 shadow-xl rounded-md ">
      <h1 className="text-2xl font-bold mb-4">Income Tax Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Assessment Year</label>
            <select
              value={assessmentYear}
              onChange={(e) => setAssessmentYear(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="2021-2022">2021-2022</option>
              <option value="2022-2023">2022-2023</option>
              <option value="2023-2024">2023-2024</option>
              <option value="2024-2025">2024-2025</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Age Category</label>
            <select
              value={ageCategory}
              onChange={(e) => setAgeCategory(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="below 60">Below 60</option>
              <option value="60 or above 60">60 or above 60</option>
              <option value="80 or above 80">80 or above 80</option>
            </select>
          </div>
          <div className="mb-4">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => toggleSection('income')}
            >
              <span className="text-xl font-bold mr-2 text-gray-700">
                {activeSection === 'income' ? '-' : '+'}
              </span>
              <h2 className=" text-gray-700 text-xl font-semibold">Income</h2>
            </div>
            {activeSection === 'income' && (
              <div>
                {Object.keys(income).map((key) => (
                  <div key={key} className="mb-2">
                    <label className="block text-gray-700 font-semibold capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                    <input
                      type="number"
                      name={key}
                      value={income[key]}
                      onChange={handleIncomeChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mb-4">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => toggleSection('deductions')}
            >
              <span className="text-xl font-bold mr-2 text-gray-700">
                {activeSection === 'deductions' ? '-' : '+'}
              </span>
              <h2 className="text-gray-700 text-xl font-semibold">Deductions</h2>
            </div>
            {activeSection === 'deductions' && (
              <div>
                {Object.keys(deductions).map((key) => (
                  <div key={key} className="mb-2">
                    <label className="block text-gray-700 font-semibold capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                    <input
                      type="number"
                      name={key}
                      value={deductions[key]}
                      onChange={handleDeductionsChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mb-4">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => toggleSection('hraExemption')}
            >
              <span className="text-xl font-bold mr-2 text-gray-700">
                {activeSection === 'hraExemption' ? '-' : '+'}
              </span>
              <h2 className="text-gray-700 text-xl font-semibold">HRA Exemption</h2>
            </div>
            {activeSection === 'hraExemption' && (
              <div>
                {Object.keys(hraExemption).map((key) => (
                  <div key={key} className="mb-2">
                    <label className="block text-gray-700 font-semibold capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                    <input
                      type="number"
                      name={key}
                      value={hraExemption[key]}
                      onChange={handleHRAExemptionChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={calculateTax}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Total Tax (Old Regime): ₹ {results.oldRegimeTax}</p>
            <p>Total Tax (New Regime): ₹ {results.newRegimeTax}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is an Income Tax Calculator?
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            An Income Tax Calculator is an online utility that computes the amount of tax payable based on the user's income details, deductions, exemptions, and the chosen tax regime. It simplifies the complex process of tax computation, providing immediate results that aid in financial decision-making.
          </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              📈 Benefits of Using an Income Tax Calculator
            </h2>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Accuracy in Tax Computation</strong><br />Manual calculations are prone to errors, especially with the intricate tax structures. An online calculator ensures precise computation, minimizing the risk of miscalculations.</li>
              <li><strong>Time Efficiency</strong><br />The calculator provides instant results, saving valuable time compared to manual computations or consulting with tax professionals.</li>
              <li><strong>Comparison Between Tax Regimes</strong><br />Users can evaluate their tax liabilities under both the old and new tax regimes, enabling them to choose the most beneficial option.</li>
              <li><strong>Financial Planning</strong><br />By understanding potential tax liabilities, individuals can plan investments and expenses more effectively to optimize tax savings.</li>
              <li><strong>Accessibility</strong><br />Being an online tool, it is accessible anytime and anywhere, providing flexibility for users to compute taxes at their convenience.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              🧾 How to Use an Income Tax Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Select the Assessment Year:</strong> Choose the financial year for which you want to calculate taxes (e.g., FY 2025-26 corresponds to AY 2026-27).</li>
              <li><strong>Enter Personal Details:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Age:</strong> Tax slabs vary based on age categories: below 60 years, 60-80 years (senior citizens), and above 80 years (super senior citizens).</li>
                  <li><strong>Residential Status:</strong> Resident or Non-Resident Indian (NRI).</li>
                </ul>
              </li>
              <li><strong>Input Income Details:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Salary Income:</strong> Gross salary before deductions.</li>
                  <li><strong>Other Income:</strong> Income from house property, interest, capital gains, etc.</li>
                </ul>
              </li>
              <li><strong>Declare Deductions and Exemptions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Section 80C:</strong> Investments in PPF, EPF, LIC premiums, etc.</li>
                  <li><strong>Section 80D:</strong> Medical insurance premiums.</li>
                  <li><strong>Section 24(b):</strong> Home loan interest.</li>
                  <li><strong>Others:</strong> Sections 80E, 80G, 80TTA, etc.</li>
                </ul>
              </li>
              <li><strong>Choose Tax Regime:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Old Regime:</strong> Allows various deductions and exemptions.</li>
                  <li><strong>New Regime:</strong> Lower tax rates with no deductions/exemptions.</li>
                </ul>
              </li>
              <li><strong>Compute Tax:</strong> The calculator will display the tax liability based on the provided information.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Budget 2025 Updates on Income Tax
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Union Budget 2025 introduced several significant changes in the income tax regime. Here are the key highlights -
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>The income tax rebate was hiked to ₹60,000 from the previous rebate limit of ₹25,000. Meaning, no tax liability on income up to ₹12 lakh under the new tax regime.</li>
              <li>The TDS limit on rent has been increased to ₹50,000 per month for FY 2025-26.</li>
              <li>The limit on tax deduction on interest for senior citizens has been increased from ₹50,000 to ₹1,00,000.</li>
              <li>In the case of Tax Collected at Source (TCS) on remittances under the Reserve Bank of India’s (RBI) Liberalized Remittance Scheme, the threshold has been increased to ₹10 Lakhs from the earlier limit of ₹7 Lakhs. Furthermore, TCS has been removed for education loans acquired to specified financial institutions.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Latest Income Tax Slabs for FY 2025-26 (AY 2026-27)
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>New Tax Regime:</strong>
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Income Range</th>
                    <th className="px-4 py-2 text-left">Tax Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">Up to ₹4,00,000</td>
                    <td className="px-4 py-2">Nil</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹4,00,001 – ₹8,00,000</td>
                    <td className="px-4 py-2">5%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹8,00,001 – ₹12,00,000</td>
                    <td className="px-4 py-2">10%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹12,00,001 – ₹16,00,000</td>
                    <td className="px-4 py-2">15%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹16,00,001 – ₹20,00,000</td>
                    <td className="px-4 py-2">20%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹20,00,001 – ₹24,00,000</td>
                    <td className="px-4 py-2">25%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Above ₹24,00,000</td>
                    <td className="px-4 py-2">30%</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            {/* Desktop Table */}
<div className="hidden sm:block mt-4">
  <table className="w-full table-auto border-collapse border border-gray-300">
    <thead className="bg-gray-800 text-white">
      <tr>
        <th className="px-4 py-2 text-left">Income Range</th>
        <th className="px-4 py-2 text-left">Tax Rate</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-300 text-gray-700">
      <tr><td className="px-4 py-2">Up to ₹4,00,000</td><td className="px-4 py-2">Nil</td></tr>
      <tr><td className="px-4 py-2">₹4,00,001 – ₹8,00,000</td><td className="px-4 py-2">5%</td></tr>
      <tr><td className="px-4 py-2">₹8,00,001 – ₹12,00,000</td><td className="px-4 py-2">10%</td></tr>
      <tr><td className="px-4 py-2">₹12,00,001 – ₹16,00,000</td><td className="px-4 py-2">15%</td></tr>
      <tr><td className="px-4 py-2">₹16,00,001 – ₹20,00,000</td><td className="px-4 py-2">20%</td></tr>
      <tr><td className="px-4 py-2">₹20,00,001 – ₹24,00,000</td><td className="px-4 py-2">25%</td></tr>
      <tr><td className="px-4 py-2">Above ₹24,00,000</td><td className="px-4 py-2">30%</td></tr>
    </tbody>
  </table>
</div>

{/* Mobile Card View */}
<div className="block sm:hidden mt-4 space-y-4">
  {[
    { range: "Up to ₹4,00,000", rate: "Nil" },
    { range: "₹4,00,001 – ₹8,00,000", rate: "5%" },
    { range: "₹8,00,001 – ₹12,00,000", rate: "10%" },
    { range: "₹12,00,001 – ₹16,00,000", rate: "15%" },
    { range: "₹16,00,001 – ₹20,00,000", rate: "20%" },
    { range: "₹20,00,001 – ₹24,00,000", rate: "25%" },
    { range: "Above ₹24,00,000", rate: "30%" },
  ].map((item, index) => (
    <div key={index} className="border border-gray-300 rounded-md p-4 bg-white shadow-sm">
      <div className="font-semibold text-gray-800 mb-1">Income Range:</div>
      <div className="text-gray-700 mb-2">{item.range}</div>
      <div className="font-semibold text-gray-800">Tax Rate:</div>
      <div className="text-gray-700">{item.rate}</div>
    </div>
  ))}
</div>

            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <em>*Note: Under Section 87A, a rebate is available for taxable incomes up to ₹12 lakh, resulting in zero tax liability for such individuals.</em>
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Old Tax Regime:</strong>
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Income Range</th>
                    <th className="px-4 py-2 text-left">Tax Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">Up to ₹2,50,000</td>
                    <td className="px-4 py-2">Nil</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹2,50,001 – ₹5,00,000</td>
                    <td className="px-4 py-2">5%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">₹5,00,001 – ₹10,00,000</td>
                    <td className="px-4 py-2">20%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Above ₹10,00,000</td>
                    <td className="px-4 py-2">30%</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            {/* Desktop Table View */}
<div className="hidden sm:block mt-4">
  <table className="w-full table-auto border-collapse border border-gray-300">
    <thead className="bg-gray-800 text-white">
      <tr>
        <th className="px-4 py-2 text-left">Income Range</th>
        <th className="px-4 py-2 text-left">Tax Rate</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-300 text-gray-700">
      <tr><td className="px-4 py-2">Up to ₹2,50,000</td><td className="px-4 py-2">Nil</td></tr>
      <tr><td className="px-4 py-2">₹2,50,001 – ₹5,00,000</td><td className="px-4 py-2">5%</td></tr>
      <tr><td className="px-4 py-2">₹5,00,001 – ₹10,00,000</td><td className="px-4 py-2">20%</td></tr>
      <tr><td className="px-4 py-2">Above ₹10,00,000</td><td className="px-4 py-2">30%</td></tr>
    </tbody>
  </table>
</div>

{/* Mobile Card View */}
<div className="block sm:hidden mt-4 space-y-4">
  {[
    { range: "Up to ₹2,50,000", rate: "Nil" },
    { range: "₹2,50,001 – ₹5,00,000", rate: "5%" },
    { range: "₹5,00,001 – ₹10,00,000", rate: "20%" },
    { range: "Above ₹10,00,000", rate: "30%" },
  ].map((item, index) => (
    <div key={index} className="border border-gray-300 rounded-md p-4 bg-white shadow-sm">
      <div className="font-semibold text-gray-800 mb-1">Income Range:</div>
      <div className="text-gray-700 mb-2">{item.range}</div>
      <div className="font-semibold text-gray-800">Tax Rate:</div>
      <div className="text-gray-700">{item.rate}</div>
    </div>
  ))}
</div>

            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <em>*Note: Senior citizens (60-80 years) and super senior citizens (above 80 years) have higher basic exemption limits of ₹3,00,000 and ₹5,00,000 respectively.</em>
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Income Tax Calculator?
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Effortless Calculations</strong><br />No more spreadsheets or confusing tax tables. Get accurate tax estimates instantly by entering your income and deduction details.</li>
              <li><strong>Stay Tax Compliant</strong><br />Ensure you're paying the correct amount of tax. Our calculator adheres to the latest income tax slabs and rules as per the Government of India.</li>
              <li><strong>Compare Old vs. New Tax Regimes</strong><br />Not sure which tax regime benefits you more? TheFinMaster’s calculator provides a side-by-side comparison so you can choose the one that saves you the most.</li>
              <li><strong>Plan Ahead & Save More</strong><br />By estimating your tax early, you can make smart investment decisions to maximize deductions and reduce your overall tax burden.</li>
              <li><strong>Beginner-Friendly Interface</strong><br />Even if you’re unfamiliar with taxes, our step-by-step input fields guide you smoothly through the process.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Example: How The Calculator Works in Practice
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s take the case of Rohan, a 30-year-old salaried professional living in Mumbai.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>🧾 Rohan's Financial Details:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Annual Salary (CTC): ₹12,00,000</li>
              <li>Standard Deduction: ₹50,000 (automatically applied)</li>
              <li>HRA Exemption: ₹1,20,000</li>
              <li>Investments under Section 80C: ₹1,50,000 (EPF + PPF + ELSS)</li>
              <li>NPS Contribution (80CCD(1B)): ₹50,000</li>
              <li>Health Insurance Premium (80D): ₹25,000</li>
              <li>Savings Account Interest (80TTA): ₹8,000</li>
              <li>Education Loan Interest (80E): ₹40,000</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Step-by-Step Calculation Using The Calculator:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Gross Income: ₹12,00,000</li>
              <li>Less: Standard Deduction: ₹50,000 → ₹11,50,000</li>
              <li>Less: HRA Exemption: ₹1,20,000 → ₹10,30,000</li>
              <li><strong>Deductions Applied:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Section 80C: ₹1,50,000</li>
                  <li>Section 80CCD(1B): ₹50,000</li>
                  <li>Section 80D: ₹25,000</li>
                  <li>Section 80TTA: ₹8,000</li>
                  <li>Section 80E: ₹40,000</li>
                </ul>
              </li>
              <li><strong>🧾 Total Deductions:</strong> ₹2,73,000</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Taxable Income (Old Regime):</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              ₹10,30,000 - ₹2,73,000 = ₹7,57,000
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Tax Calculation (Old Regime):</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>₹2,50,000 → Nil</li>
              <li>₹2,50,001 – ₹5,00,000 → 5% = ₹12,500</li>
              <li>₹5,00,001 – ₹7,57,000 → 20% = ₹51,400</li>
            </ul>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Total Tax (Old Regime):</strong> ₹63,900 + 4% Cess = ₹66,456
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>📊 Tax Calculation (New Regime):</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>No deductions considered</li>
              <li>Income: ₹10,30,000</li>
              <li>Applicable slab rate = approx ₹70,000 (based on new slab structure)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>✅ Conclusion:</strong> Old regime is more beneficial for Rohan.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Understanding Common Income Tax Deductions
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Our calculator lets you apply major deductions that can significantly reduce your taxable income. Here's what they mean:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>🏡 Basic Deductions under Section 80C</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Limit:</strong> ₹1.5 lakh per year
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Includes investments and expenses like:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Employee Provident Fund (EPF)</li>
              <li>Life Insurance Premiums</li>
              <li>Public Provident Fund (PPF)</li>
              <li>ELSS Mutual Funds</li>
              <li>Principal Repayment of Home Loan</li>
              <li>Tuition Fees for Children</li>
            </ul>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> It’s the most commonly used deduction for salaried individuals to reduce tax liability.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>🏦 Contribution to NPS under Section 80CCD(1B)</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Limit:</strong> ₹50,000 per year (in addition to 80C)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Applicable for contributions made to the National Pension System (NPS).
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> This is an additional deduction beyond Section 80C and offers dual benefits—tax saving + retirement planning.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>🏥 Medical Insurance Premium under Section 80D</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Limits:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>₹25,000 for self/family</li>
              <li>Additional ₹25,000 for parents (₹50,000 if parents are senior citizens)</li>
            </ul>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Covers premiums paid towards medical/health insurance for self, spouse, children, and parents.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> Encourages individuals to invest in health coverage while offering tax relief.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>💝 Donations under Section 80G</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You can claim deductions for donations made to registered charitable institutions. The deduction can be 50% or 100%, depending on the organization and whether there's a qualifying limit.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> It promotes philanthropic activities while reducing your taxable income.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>🎓 Education Loan Interest under Section 80E</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Allows full deduction of interest paid on an education loan for self, spouse, or children for up to 8 years.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> Encourages higher education without adding a tax burden during repayment.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>💰 Interest on Savings under Section 80TTA/80TTB</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>80TTA (below 60 years):</strong> Deduction of up to ₹10,000 on savings account interest.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>80TTB (senior citizens):</strong> Deduction of up to ₹50,000 on interest from savings + fixed deposits.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Why it matters:</strong> Offers tax relief on passive income for all and extra for seniors.
            </p>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                         {/* Ad Banner */}
                      <div className="mt-4">
                        <div className="bg-white shadow-md rounded-lg overflow-hidden">
                          <div className="p-3 w-full h-auto rounded-md object-cover">
                            <AdBanner displayOrder="2" />
                          </div>
                        </div>
                      </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default IncomeTaxCalculator;
